"use client";

import { useState, useEffect, Suspense } from "react";
import { usePathname } from "next/navigation";
import { useDispatch } from "react-redux";
import { getUserFromCookies } from "../utils/cookieAuth";
import { setUser } from "../redux/slices/userSlice";
import { Sidebar, DashboardHeader } from "./dashboard/layout/components";
import ProgressBar from "./ui/progress-bar";
import Header from "./Header";
import Footer from "./Footer";
import PwaInstallBanner from "./PwaInstallBanner";
import { cn } from "../lib/utils";
import { NotificationProvider } from "../contexts/NotificationContext";

export default function ConditionalLayout({ children }) {
  const pathname = usePathname();
  const dispatch = useDispatch();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Check if current path is an auth route or dashboard route
  const isAuthRoute = pathname === "/signup" || pathname === "/dashboard/login";
  const isDashboardRoute = pathname.startsWith("/dashboard");
  const isTrackingRoute = pathname.includes("/tracking/");

  // Sync user data from cookies to Redux on mount (dashboard only)
  useEffect(() => {
    if (!isDashboardRoute) return;

    const userData = getUserFromCookies();
    if (userData) {
      dispatch(setUser(userData));
    }

    const handleUserUpdate = () => {
      const updatedUserData = getUserFromCookies();
      if (updatedUserData) {
        dispatch(setUser(updatedUserData));
      }
    };

    window.addEventListener('userProfileUpdated', handleUserUpdate);
    return () => window.removeEventListener('userProfileUpdated', handleUserUpdate);
  }, [dispatch, isDashboardRoute]);

  // Close sidebar on desktop resize
  useEffect(() => {
    if (!isDashboardRoute) return;

    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isDashboardRoute]);

  // Auth routes - no shell
  if (isAuthRoute) {
    return <>{children}</>;
  }

  // Dashboard routes - sidebar + header shell (like website Header/Footer)
  if (isDashboardRoute) {
    return (
      <NotificationProvider>
        <div className="flex h-dvh w-full  bg-background font-poppins">
          <Suspense fallback={null}>
            <ProgressBar />
          </Suspense>

          <Sidebar
            isOpen={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
          />

          <div className="flex-1 flex flex-col min-w-0 ">
            {/* Dashboard Header - Fixed at top */}
            <div className="flex-shrink-0 z-30">
              <DashboardHeader onMenuClick={() => setSidebarOpen(true)} />
            </div>

            {/* Page content - Scrollable area */}
            <main
              className={cn(
                "flex-1 min-h-0 ",
                isTrackingRoute ? "overflow-y-hidden" : "overflow-y-auto",
                "[&::-webkit-scrollbar]:w-2",
                "[&::-webkit-scrollbar-track]:bg-transparent",
                "[&::-webkit-scrollbar-thumb]:bg-gray-300",
                "[&::-webkit-scrollbar-thumb]:rounded-full",
                "[&::-webkit-scrollbar-thumb]:hover:bg-gray-400",
                "bg-background"
              )}
            >
              {children}
            </main>
          </div>
        </div>
      </NotificationProvider>
    );
  }

  // Normal website routes - header + footer
  return (
    <>
      <Header />
      <main className="relative  ">{children}</main>
      <Footer />
      <PwaInstallBanner />
    </>
  );
}